import { AxiosResponse } from 'axios';
import React, { createContext, useState, ReactNode, useContext, useCallback, useEffect, useRef } from 'react';

import { signIn, signUp } from '../api/auth';
import { getUserInfo } from '../api/user';
import { getUserProfiles, setDefaultProfile, deleteCurrentProfile } from '../api/profile';
import { IUser } from '../types/User';
import { IProfile } from '../types/Profile';

// Extract constants
const STORAGE_KEYS = {
  AUTH_TOKEN: 'authToken',
  REFRESH_TOKEN: 'refreshToken',
  ACTIVE_PROFILE: 'activeProfile',
  USER: 'user',
} as const;

// Extract type definition
interface AuthContextType {
  user: IUser | null;
  profiles: IProfile[];
  activeProfile: IProfile | null;
  authToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, confirmPassword: string) => Promise<void>;
  logout: () => void;
  setProfile: (profile: IProfile) => Promise<void>;
  switchProfile: (profile: IProfile) => Promise<void>;
  deleteProfile: (profileId: string) => Promise<AxiosResponse | null>;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  isInitialized: boolean;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<IUser | null>(null);
  const [profiles, setProfiles] = useState<IProfile[]>([]);
  const [activeProfile, setActiveProfile] = useState<IProfile | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Use ref to prevent infinite loop in useEffect
  const initRef = useRef(false);

  const clearError = () => setError(null);
  const isAuthenticated = !!authToken && !!user;

  // Pure helper to determine which profile to select (no state mutations)
  const selectProfile = useCallback((profilesData: IProfile[]): IProfile | null => {
    if (profilesData.length === 0) {
      return null;
    }

    const storedProfile = localStorage.getItem(STORAGE_KEYS.ACTIVE_PROFILE);
    const storedProfileData = storedProfile ? JSON.parse(storedProfile) : null;
    const defaultProfile = profilesData.find(profile => profile.isDefault);

    if (storedProfileData) {
      const matchedProfile = profilesData.find(profile => profile.token === storedProfileData.token);
      return matchedProfile || defaultProfile || profilesData[0];
    } else {
      return defaultProfile || profilesData[0];
    }
  }, []);

  // Stable helper to handle profile selection logic (sets state + localStorage)
  const handleProfileSelection = useCallback(
    (profilesData: IProfile[]) => {
      const selected = selectProfile(profilesData);
      setActiveProfile(selected);
      if (selected) {
        localStorage.setItem(STORAGE_KEYS.ACTIVE_PROFILE, JSON.stringify(selected));
      }
    },
    [selectProfile],
  );

  // Load user profiles
  const loadProfiles = useCallback(async () => {
    try {
      const profilesData = await getUserProfiles();
      setProfiles(profilesData);
      handleProfileSelection(profilesData);
    } catch (err) {
      setError('Failed to load profiles');
      throw err;
    }
  }, [handleProfileSelection]);

  // Handle successful authentication
  // Fetches ALL data first, then sets ALL state in a single synchronous block
  // to avoid intermediate renders with incomplete auth state.
  const handleAuthSuccess = useCallback(
    async (accessToken: string, refreshToken: string) => {
      // Store tokens in localStorage immediately so API calls can use them
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, accessToken);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      // Set authToken so the axios interceptor picks it up for subsequent calls
      setAuthToken(accessToken);

      try {
        // Fetch all data first (no React state mutations yet)
        const userData = await getUserInfo();
        const profilesData = await getUserProfiles();
        const selectedProfile = selectProfile(profilesData);

        // Batch all state updates in a single synchronous block → single React render
        setUser(userData);
        setProfiles(profilesData);
        setActiveProfile(selectedProfile);

        // Persist to localStorage
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
        if (selectedProfile) {
          localStorage.setItem(STORAGE_KEYS.ACTIVE_PROFILE, JSON.stringify(selectedProfile));
        }
      } catch (err) {
        setError('Failed to get user information');
        // Clean up on failure — clear both token and user to avoid inconsistent state
        setAuthToken(null);
        setUser(null);
        localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER);
        throw err;
      }
    },
    [selectProfile],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      setLoading(true);
      clearError();
      try {
        const authResponse = await signIn({ email, password });
        await handleAuthSuccess(authResponse.access_token, authResponse.refresh_token);
      } catch (err) {
        setError('Login failed. Please check your credentials.');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [handleAuthSuccess],
  );

  const signup = useCallback(
    async (email: string, password: string, confirmPassword: string) => {
      setLoading(true);
      clearError();
      try {
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          throw new Error('Passwords do not match');
        }
        const authResponse = await signUp({ email, password });
        await handleAuthSuccess(authResponse.access_token, authResponse.refresh_token);
      } catch (err) {
        if (error !== 'Passwords do not match') {
          setError('Signup failed. Please try again.');
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [handleAuthSuccess, error],
  );

  const logout = useCallback(() => {
    setUser(null);
    setAuthToken(null);
    setProfiles([]);
    // Keep activeProfile in localStorage for guest users
    const currentProfile = activeProfile;
    localStorage.clear();
    if (currentProfile && !isAuthenticated) {
      localStorage.setItem(STORAGE_KEYS.ACTIVE_PROFILE, JSON.stringify(currentProfile));
      setActiveProfile(currentProfile);
    } else {
      setActiveProfile(null);
    }
  }, [activeProfile, isAuthenticated]);

  const setProfile = useCallback(
    async (profile: IProfile) => {
      setActiveProfile(profile);
      localStorage.setItem(STORAGE_KEYS.ACTIVE_PROFILE, JSON.stringify(profile));

      if (isAuthenticated) {
        await setDefaultProfile(profile.token);
        await loadProfiles();
      }
    },
    [loadProfiles, isAuthenticated],
  );

  const switchProfile = useCallback(async (profile: IProfile) => {
    await setDefaultProfile(profile.token);
    setActiveProfile(profile);
    localStorage.setItem(STORAGE_KEYS.ACTIVE_PROFILE, JSON.stringify(profile));
  }, []);

  const deleteProfile = useCallback(
    async (profileToken: string) => {
      try {
        const response = await deleteCurrentProfile(profileToken);
        if (response.status === 204) {
          const newProfiles = await getUserProfiles();
          setProfiles(newProfiles);

          // If deleting the active profile, clear it
          if (activeProfile?.token === profileToken) {
            if (newProfiles.length > 0) {
              const newActiveProfile = newProfiles.find(p => p.isDefault) || newProfiles[0];
              setActiveProfile(newActiveProfile);
              localStorage.setItem(STORAGE_KEYS.ACTIVE_PROFILE, JSON.stringify(newActiveProfile));
            } else {
              setActiveProfile(null);
              localStorage.removeItem(STORAGE_KEYS.ACTIVE_PROFILE);
            }
          }
          return response;
        } else {
          setError('Failed to delete profile. Please try again.');
          return null;
        }
      } catch {
        setError('Something went wrong. Please try again later.');
        return null;
      }
    },
    [activeProfile],
  );

  // Initialize from localStorage - runs only once on mount
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const initialize = async () => {
      const storedAuthToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const storedActiveProfile = localStorage.getItem(STORAGE_KEYS.ACTIVE_PROFILE);

      if (storedAuthToken) {
        setAuthToken(storedAuthToken);

        // Validate token by fetching user info, regardless of cached user data
        try {
          const userData = await getUserInfo();
          setUser(userData);
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
        } catch {
          // Token is invalid, clear auth
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.USER);
          setAuthToken(null);
          setIsInitialized(true);
          return;
        }

        // Load profiles for authenticated user
        try {
          await loadProfiles();
        } catch {
          // Profiles failed to load but user is authenticated
          setError('Failed to load profiles');
        }
      }

      // Load guest profile if exists
      if (!storedAuthToken && storedActiveProfile) {
        try {
          setActiveProfile(JSON.parse(storedActiveProfile));
        } catch {
          // Invalid stored profile, ignore
          localStorage.removeItem(STORAGE_KEYS.ACTIVE_PROFILE);
        }
      }

      setIsInitialized(true);
    };

    initialize();
  }, [loadProfiles]);

  return (
    <AuthContext.Provider
      value={{
        user,
        profiles,
        activeProfile,
        authToken,
        login,
        signup,
        logout,
        setProfile,
        switchProfile,
        deleteProfile,
        isAuthenticated,
        loading,
        error,
        isInitialized,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
